import { type ThemeConfig, theme } from 'antd';

type ThemeTokens = NonNullable<ThemeConfig['token']>;

const sharedThemeTokens: ThemeTokens = {
  // Brand
  colorPrimary: '#1677ff',
  colorPrimaryHover: '#4096ff',
  colorLink: '#1677ff',
  colorLinkHover: '#4096ff',

  // Semantic
  colorSuccess: '#24c68b',
  colorSuccessHover: '#42d6a2',
  colorWarning: '#d99b00',
  colorError: '#ff4d4f',

  // Shape
  borderRadius: 12,
  borderRadiusLG: 20,

  // Uniform control height for all form controls (inputs, selects, buttons, pickers…).
  controlHeight: 32,

  // Typography
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};

const lightThemeTokens: ThemeTokens = {
  // Layout and surfaces
  colorBgLayout: '#f3f6fb',
  colorBgContainer: '#ffffff',
  colorBgElevated: '#ffffff',
  colorBgContainerDisabled: '#e8edf5',

  // Text
  colorText: '#13213a',
  colorTextSecondary: '#62708a',
  colorTextTertiary: '#8b97aa',

  // Borders and fills
  colorBorder: '#d9e1ec',
  colorBorderSecondary: '#e7ecf3',
  colorFillTertiary: 'rgba(19, 33, 58, 0.06)',
  colorFillQuaternary: 'rgba(19, 33, 58, 0.03)',

  // Semantic backgrounds
  colorPrimaryBg: 'rgba(22, 119, 255, 0.10)',
  colorSuccessBg: 'rgba(36, 198, 139, 0.10)',
  colorWarningBg: 'rgba(217, 155, 0, 0.08)',
  colorErrorBg: 'rgba(255, 77, 79, 0.08)',
};

const darkThemeTokens: ThemeTokens = {
  // Layout and surfaces
  colorBgLayout: '#070c19',
  colorBgContainer: '#111b2d',
  colorBgElevated: '#18253d',
  colorBgContainerDisabled: '#172238',

  // Text
  colorText: '#f4f7ff',
  colorTextSecondary: '#98a5bd',
  colorTextTertiary: '#6e7a91',

  // Borders and fills
  colorBorder: 'rgba(255, 255, 255, 0.14)',
  colorBorderSecondary: 'rgba(255, 255, 255, 0.10)',
  colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
  colorFillQuaternary: 'rgba(255, 255, 255, 0.05)',

  // Semantic backgrounds
  colorPrimaryBg: 'rgba(22, 119, 255, 0.18)',
  colorSuccessBg: 'rgba(36, 198, 139, 0.12)',
  colorWarningBg: 'rgba(217, 155, 0, 0.12)',
  colorErrorBg: 'rgba(255, 77, 79, 0.12)',
};

// Muted prefix-icon color for inputs — resolves via antd's cssVar so it adapts to light/dark theme
// (the old hardcoded rgba(0,0,0,0.25) was near-invisible on dark backgrounds).
export const MUTED_ICON_COLOR = 'var(--ant-color-text-placeholder)';

export function buildTheme(overrides?: ThemeTokens, dark = false): ThemeConfig {
  return {
    algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,

    token: {
      ...sharedThemeTokens,
      ...(dark ? darkThemeTokens : lightThemeTokens),
      ...(overrides ?? {}),
    },

    components: {
      Form: {
        itemMarginBottom: 16,
      },
      // Dark mode's default track (colorBgLayout) renders as a visible pill; keep it transparent
      // so the compact selector matches the backgroundless look it already has in light mode.
      Segmented: {
        trackBg: dark ? 'transparent' : undefined,
      },
      Switch: {
        colorPrimary: '#1677ff',
        colorPrimaryHover: '#4096ff',
      },
    },

    cssVar: true,
    hashed: false,
  };
}
